import {
    useEffect,
    useRef,
    createContext,
    ReactNode,
    useState,
    useContext
} from 'react';

import { useRobotDetails } from '../../utilities/RobotDetailsContext';

// A dummy non-functional WebSocket to satisfy non-null requirement
const dummyWebSocket = new WebSocket('ws://localhost/dummy');

interface SocketContextType {
    ws: WebSocket;
    lastMessage: MessageEvent | null;
}

export const SocketContext = createContext<SocketContextType | undefined>({
    ws: dummyWebSocket,
    lastMessage: null,
});

interface ISocketProvider {
    children: ReactNode;
}

export const RealtimeUpdatesProvider = ({ children }: ISocketProvider) => {
    const { robotDetails } = useRobotDetails();
    const wsRef = useRef<WebSocket>(dummyWebSocket);
    const timeout = useRef<NodeJS.Timeout | null>(null);
    const [lastMessage, setLastMessage] = useState<MessageEvent | null>(null);

    useEffect(() => {
        const connect = () => {
            const ws = new WebSocket(robotDetails.realTimeUpdatesUrl);
            wsRef.current = ws;

            ws.onopen = () => {
                console.log('WebSocket connected');
            };

            ws.onmessage = (event: MessageEvent) => {
                console.log('WebSocket message received:', event.data);
                setLastMessage(event);
            };

            ws.onerror = (event: Event) => {
                console.error('WebSocket error:', event);
            };

            ws.onclose = () => {
                console.warn('WebSocket closed. Reconnecting in 3 seconds...');
                if (timeout.current) clearTimeout(timeout.current);
                timeout.current = setTimeout(connect, 3000);
            };
        };

        connect();

        return () => {
            if (timeout.current) clearTimeout(timeout.current);
            if (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING) {
                wsRef.current.close();
            }
        };
    }, [robotDetails.realTimeUpdatesUrl]);

    return (
        <SocketContext.Provider value={{ ws: wsRef.current, lastMessage }}>
            {children}
        </SocketContext.Provider>
    );
};

export const useRealtimeUpdates = () => {
    const context = useContext(SocketContext);
    if (!context) {
        throw new Error('useRealtimeUpdates must be used within a RealtimeUpdatesProvider');
    }
    return context;
};
